import { supabase } from '../supabase/client';
import { BlacklistAuditLog, BlacklistEntry, BlacklistSummary, BlacklistStatus } from '../../types';

export interface BlacklistQueryOptions {
  status?: BlacklistStatus;
  search?: string;
  includeExpired?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreateBlacklistPayload {
  userId?: string | null;
  relatedCraftsmanId?: string | null;
  relatedCustomerId?: string | null;
  relatedCompanyId?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  subjectType: BlacklistEntry['subjectType'];
  status?: BlacklistStatus;
  source: BlacklistEntry['source'];
  reason?: string | null;
  evidenceUrls?: string[];
  riskScore?: number | null;
  expiresAt?: string | null;
  appealDeadline?: string | null;
  appealReference?: string | null;
  notes?: string | null;
  metadata?: Record<string, any>;
}

export interface UpdateBlacklistPayload {
  status?: BlacklistStatus;
  reason?: string | null;
  evidenceUrls?: string[];
  riskScore?: number | null;
  expiresAt?: string | null;
  appealDeadline?: string | null;
  appealReference?: string | null;
  notes?: string | null;
  metadata?: Record<string, any>;
}

const mapEntry = (row: any): BlacklistEntry => ({
  id: row.id,
  userId: row.user_id,
  relatedCraftsmanId: row.related_craftsman_id,
  relatedCustomerId: row.related_customer_id,
  relatedCompanyId: row.related_company_id,
  contactEmail: row.contact_email,
  contactPhone: row.contact_phone,
  subjectType: row.subject_type,
  status: row.status,
  source: row.source,
  reason: row.reason,
  evidenceUrls: row.evidence_urls ?? [],
  riskScore: row.risk_score,
  metadata: row.metadata ?? {},
  flaggedBy: row.flagged_by,
  expiresAt: row.expires_at,
  appealDeadline: row.appeal_deadline,
  appealReference: row.appeal_reference,
  notes: row.notes,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapAuditLog = (row: any): BlacklistAuditLog => ({
  id: row.id,
  blacklistEntryId: row.blacklist_entry_id,
  action: row.action,
  performedBy: row.performed_by,
  notes: row.notes,
  evidenceUrls: row.evidence_urls ?? [],
  createdAt: row.created_at,
});

const mapSummary = (row: any): BlacklistSummary => ({
  active: Number(row.active ?? 0),
  pendingReview: Number(row.pending_review ?? 0),
  appeals: Number(row.appeals ?? 0),
  expiringSoon: Number(row.expiring_soon ?? 0),
  total: Number(row.total ?? 0),
  turnkeyRelated: Number(row.turnkey_related ?? 0),
  customerDisputes: Number(row.customer_disputes ?? 0),
  warrantyIssues: Number(row.warranty_issues ?? 0),
});

export class BlacklistService {
  static async getEntries(options: BlacklistQueryOptions = {}): Promise<{
    entries: BlacklistEntry[];
    error: string | null;
  }> {
    try {
      let query = supabase
        .from('fraud_blacklist_entries')
        .select('*')
        .order('updated_at', { ascending: false });

      if (options.status) {
        query = query.eq('status', options.status);
      } else if (!options.includeExpired) {
        query = query.neq('status', 'expired');
      }

      if (options.search) {
        const term = `%${options.search.trim()}%`;
        query = query.or(
          `contact_email.ilike.${term},contact_phone.ilike.${term},reason.ilike.${term}`
        );
      }

      if (options.limit) {
        query = query.limit(options.limit, { foreignTable: undefined });
      }

      if (options.offset) {
        query = query.range(options.offset, (options.offset + (options.limit || 50)) - 1);
      }

      const { data, error } = await query;

      if (error) {
        return { entries: [], error: error.message };
      }

      return { entries: (data || []).map(mapEntry), error: null };
    } catch (error) {
      console.error('Failed to load blacklist entries', error);
      return { entries: [], error: '無法載入黑名單資料' };
    }
  }

  static async getEntryById(id: string): Promise<{
    entry: BlacklistEntry | null;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('fraud_blacklist_entries')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        return { entry: null, error: error.message };
      }

      return { entry: data ? mapEntry(data) : null, error: null };
    } catch (error) {
      console.error('Failed to fetch blacklist entry', error);
      return { entry: null, error: '無法查詢黑名單資料' };
    }
  }

  static async createEntry(payload: CreateBlacklistPayload): Promise<{
    entry: BlacklistEntry | null;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('fraud_blacklist_entries')
        .insert({
          user_id: payload.userId ?? null,
          related_craftsman_id: payload.relatedCraftsmanId ?? null,
          related_customer_id: payload.relatedCustomerId ?? null,
          related_company_id: payload.relatedCompanyId ?? null,
          contact_email: payload.contactEmail ?? null,
          contact_phone: payload.contactPhone ?? null,
          subject_type: payload.subjectType,
          status: payload.status ?? 'pending_review',
          source: payload.source,
          reason: payload.reason ?? null,
          evidence_urls: payload.evidenceUrls ?? [],
          risk_score: payload.riskScore ?? null,
          expires_at: payload.expiresAt ?? null,
          appeal_deadline: payload.appealDeadline ?? null,
          appeal_reference: payload.appealReference ?? null,
          notes: payload.notes ?? null,
          metadata: payload.metadata ?? {},
        })
        .select('*')
        .single();

      if (error) {
        return { entry: null, error: error.message };
      }

      return { entry: data ? mapEntry(data) : null, error: null };
    } catch (error) {
      console.error('Failed to create blacklist entry', error);
      return { entry: null, error: '新增黑名單失敗' };
    }
  }

  static async updateEntry(id: string, payload: UpdateBlacklistPayload): Promise<string | null> {
    try {
      const { error } = await supabase
        .from('fraud_blacklist_entries')
        .update({
          status: payload.status,
          reason: payload.reason,
          evidence_urls: payload.evidenceUrls,
          risk_score: payload.riskScore,
          expires_at: payload.expiresAt,
          appeal_deadline: payload.appealDeadline,
          appeal_reference: payload.appealReference,
          notes: payload.notes,
          metadata: payload.metadata,
        })
        .eq('id', id);

      return error ? error.message : null;
    } catch (error) {
      console.error('Failed to update blacklist entry', error);
      return '更新黑名單失敗';
    }
  }

  static async appendAuditLog(entryId: string, log: {
    action: BlacklistAuditLog['action'];
    notes?: string;
    evidenceUrls?: string[];
  }): Promise<string | null> {
    try {
      const { error } = await supabase
        .from('fraud_blacklist_audit_logs')
        .insert({
          blacklist_entry_id: entryId,
          action: log.action,
          notes: log.notes ?? null,
          evidence_urls: log.evidenceUrls ?? [],
        });

      return error ? error.message : null;
    } catch (error) {
      console.error('Failed to append audit log', error);
      return '新增審核紀錄失敗';
    }
  }

  static async getAuditLogs(entryId: string): Promise<{
    logs: BlacklistAuditLog[];
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('fraud_blacklist_audit_logs')
        .select('*')
        .eq('blacklist_entry_id', entryId)
        .order('created_at', { ascending: false });

      if (error) {
        return { logs: [], error: error.message };
      }

      return { logs: (data || []).map(mapAuditLog), error: null };
    } catch (error) {
      console.error('Failed to load audit logs', error);
      return { logs: [], error: '無法取得審核紀錄' };
    }
  }

  static async getSummary(): Promise<{ summary: BlacklistSummary | null; error: string | null }> {
    try {
      const { data, error } = await supabase.rpc('blacklist_turnkey_summary');

      if (error) {
        return { summary: null, error: error.message };
      }

      return { summary: data ? mapSummary(data) : null, error: null };
    } catch (error) {
      console.error('Failed to calculate blacklist summary', error);
      return {
        summary: null,
        error: '無法取得黑名單統計',
      };
    }
  }

  static async getActiveEntryByUser(userId: string): Promise<{
    entry: BlacklistEntry | null;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('fraud_blacklist_entries')
        .select('*')
        .eq('user_id', userId)
        .in('status', ['pending_review', 'active', 'appeal_pending'])
        .maybeSingle();

      if (error) {
        return { entry: null, error: error.message };
      }

      return { entry: data ? mapEntry(data) : null, error: null };
    } catch (error) {
      console.error('Failed to fetch blacklist entry by user', error);
      return { entry: null, error: '無法取得黑名單狀態' };
    }
  }

  static async getActiveEntryByCraftsman(craftsmanId: string): Promise<{
    entry: BlacklistEntry | null;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('fraud_blacklist_entries')
        .select('*')
        .eq('related_craftsman_id', craftsmanId)
        .in('status', ['pending_review', 'active', 'appeal_pending'])
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        return { entry: null, error: error.message };
      }

      return { entry: data ? mapEntry(data) : null, error: null };
    } catch (error) {
      console.error('Failed to fetch blacklist entry by craftsman', error);
      return { entry: null, error: '無法取得工匠黑名單狀態' };
    }
  }

  static async getActiveEntryByCompany(companyId: string): Promise<{
    entry: BlacklistEntry | null;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('fraud_blacklist_entries')
        .select('*')
        .eq('related_company_id', companyId)
        .in('status', ['pending_review', 'active', 'appeal_pending'])
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        return { entry: null, error: error.message };
      }

      return { entry: data ? mapEntry(data) : null, error: null };
    } catch (error) {
      console.error('Failed to fetch blacklist entry by company', error);
      return { entry: null, error: '無法取得公司黑名單狀態' };
    }
  }

  /**
   * 管理員刪除「被回報的評價留言」後：建立待審黑名單（不重複若該 user／公司／工匠已有進行中列管）
   */
  static async createPendingFromModeratedReviewReply(params: {
    reportId: string;
    reviewId: string;
    replyAuthorUserId: string;
  }): Promise<{ created: boolean; skipped?: string; error?: string | null }> {
    const { reportId, reviewId, replyAuthorUserId } = params;
    if (!replyAuthorUserId || !reviewId) {
      return { created: false, skipped: 'missing_ids' };
    }

    const { data: review, error: revErr } = await supabase
      .from('reviews')
      .select('company_id, craftsman_id')
      .eq('id', reviewId)
      .maybeSingle();

    if (revErr) {
      console.warn('createPendingFromModeratedReviewReply review fetch:', revErr);
    }

    const { entry: existingUser } = await this.getActiveEntryByUser(replyAuthorUserId);
    if (existingUser) return { created: false, skipped: 'user_already_flagged' };

    if (review?.company_id) {
      const { entry: co } = await this.getActiveEntryByCompany(review.company_id);
      if (co) return { created: false, skipped: 'company_already_flagged' };
    }
    if (review?.craftsman_id) {
      const { entry: cr } = await this.getActiveEntryByCraftsman(review.craftsman_id);
      if (cr) return { created: false, skipped: 'craftsman_already_flagged' };
    }

    const { data: userRow } = await supabase
      .from('users')
      .select('id, email, phone')
      .eq('id', replyAuthorUserId)
      .maybeSingle();

    let subjectType: BlacklistEntry['subjectType'] = 'user';
    let relatedCompanyId: string | null = null;
    let relatedCraftsmanId: string | null = null;

    if (review?.company_id) {
      subjectType = 'company';
      relatedCompanyId = review.company_id;
    } else if (review?.craftsman_id) {
      subjectType = 'craftsman';
      relatedCraftsmanId = review.craftsman_id;
    }

    const reason = `評價留言違規，管理員已刪除。回報單 ${reportId}；主評價 ${reviewId}`;

    const { entry, error } = await this.createEntry({
      userId: replyAuthorUserId,
      relatedCompanyId,
      relatedCraftsmanId,
      relatedCustomerId: null,
      contactEmail: userRow?.email ?? null,
      contactPhone: (userRow as any)?.phone ?? null,
      subjectType,
      status: 'pending_review',
      source: 'customer_report',
      reason,
      riskScore: 4,
      metadata: {
        review_report_id: reportId,
        review_id: reviewId,
        trigger: 'review_reply_deleted_by_moderation',
      },
    });

    if (error || !entry) {
      return { created: false, error: error || '新增黑名單失敗' };
    }

    await this.appendAuditLog(entry.id, {
      action: 'created',
      notes: `評價回報處置後自動建立（回報單 ${reportId}）`,
    });

    return { created: true };
  }

  static async assertUserNotBlacklisted(userId: string): Promise<void> {
    const { entry, error } = await this.getActiveEntryByUser(userId);
    if (error) {
      console.warn('Blacklist check failed:', error);
      return;
    }
    if (entry) {
      throw new Error('ACCOUNT_BLACKLISTED');
    }
  }
}

